import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import {
	getCompanyProducts as getCompanyProductsAction
} from '../../redux/actions';
import { canSwitchCompany, getActiveCompany } from '../../../util/user/companySwitch';
import AccountUtil from '../../../account/components/utils/AccountUtil';
import { fetchCompanyDirectory } from '../CompanySwitch/utils/companyDirectory';
import { NO_COMPANY_PRODUCTS } from './utils/companyProducts';
import ConfirmDialog from '../shared/ConfirmDialog';
import CorrGuardShield from '../shared/CorrGuardShield';
import releaseVersion from '../layout/ReleaseVersion';

import './LeftPanel.scss';

const mapStateToProps = state => {
	const {
		main: { companyProducts }
	} = state;
	return { companyProducts };
};
const mapDispatchToProps = dispatch =>
	bindActionCreators(
		{
			getCompanyProducts: getCompanyProductsAction
		},
		dispatch
	);

// ── SVG icon helpers ────────────────────────────────────────
const IconGrid = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<rect x="3" y="3" width="7" height="7" rx="1" />
		<rect x="14" y="3" width="7" height="7" rx="1" />
		<rect x="3" y="14" width="7" height="7" rx="1" />
		<rect x="14" y="14" width="7" height="7" rx="1" />
	</svg>
);

const IconFolder = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M3 7a2 2 0 012-2h4l2 2.5H19a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
	</svg>
);

const IconCrosshair = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<circle cx="12" cy="12" r="9" />
		<circle cx="12" cy="12" r="3" />
		<line x1="12" y1="3" x2="12" y2="7" />
		<line x1="12" y1="17" x2="12" y2="21" />
		<line x1="3" y1="12" x2="7" y2="12" />
		<line x1="17" y1="12" x2="21" y2="12" />
	</svg>
);

const IconMonitor = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M12 22s-8-4.5-8-11.8A8 8 0 0112 2a8 8 0 018 8.2c0 7.3-8 11.8-8 11.8z" />
		<circle cx="12" cy="10" r="3" />
	</svg>
);

const IconUser = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
		<circle cx="12" cy="7" r="4" />
	</svg>
);

const IconHelp = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<circle cx="12" cy="12" r="10" />
		<path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
		<line x1="12" y1="17" x2="12.01" y2="17" />
	</svg>
);

const IconSignOut = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
		<polyline points="16 17 21 12 16 7" />
		<line x1="21" y1="12" x2="9" y2="12" />
	</svg>
);

const IconChevronLeft = ({ flipped }) => (
	<svg
		width="15"
		height="15"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		style={{ transform: flipped ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.28s ease' }}
	>
		<polyline points="15 18 9 12 15 6" />
	</svg>
);

const IconMenu = () => (
	<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<line x1="4" y1="6" x2="20" y2="6" />
		<line x1="4" y1="12" x2="20" y2="12" />
		<line x1="4" y1="18" x2="20" y2="18" />
	</svg>
);

const IconSliders = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<line x1="4" y1="21" x2="4" y2="14" />
		<line x1="4" y1="10" x2="4" y2="3" />
		<line x1="12" y1="21" x2="12" y2="12" />
		<line x1="12" y1="8" x2="12" y2="3" />
		<line x1="20" y1="21" x2="20" y2="16" />
		<line x1="20" y1="12" x2="20" y2="3" />
		<line x1="1" y1="14" x2="7" y2="14" />
		<line x1="9" y1="8" x2="15" y2="8" />
		<line x1="17" y1="16" x2="23" y2="16" />
	</svg>
);

// ── NavItem ─────────────────────────────────────────────────
const NavItem = ({ to, icon, label, isActive, collapsed, onClick, className }) => {
	const handleClick = useCallback(
		e => {
			if (onClick) {
				onClick(e);
			}
		},
		[onClick]
	);

	return (
		<li className={`cg-nav__item${isActive ? ' cg-nav__item--active' : ''}${className ? ` ${className}` : ''}`}>
			{to ? (
				<Link to={to} className="cg-nav__link" title={label} onClick={handleClick}>
					<span className="cg-nav__icon">{icon}</span>
					<span className="cg-nav__label">{label}</span>
					{collapsed && <span className="cg-nav__tooltip">{label}</span>}
				</Link>
			) : (
				<button type="button" className="cg-nav__link" title={label} onClick={handleClick} style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}>
					<span className="cg-nav__icon">{icon}</span>
					<span className="cg-nav__label">{label}</span>
					{collapsed && <span className="cg-nav__tooltip">{label}</span>}
				</button>
			)}
		</li>
	);
};

// ── NavSection ──────────────────────────────────────────────
const NavSection = ({ title, children, collapsed }) => (
	<div className="cg-nav__section">
		{!collapsed && title && (
			<div className="cg-nav__section-title">{title}</div>
		)}
		<ul className="cg-nav__list">{children}</ul>
	</div>
);

// ── CorrGuard logo SVG ──────────────────────────────────────
// ── LeftPanel (main component) ──────────────────────────────
const LeftPanel = ({
	getCompanyProducts,
	companyProducts,
	location
}) => {
	const [collapsed, setCollapsed] = useState(() => {
		try {
			const saved = localStorage.getItem('cg_sidebar_collapsed');
			return saved !== null ? saved === 'true' : true;
		} catch {
			return true;
		}
	});
	const [isSignOutConfirmOpen, setIsSignOutConfirmOpen] = useState(false);
	const [isMobileOpen, setIsMobileOpen] = useState(false);
	const [switchableCompanyCount, setSwitchableCompanyCount] = useState(null);
	// Which products the company being viewed actually has data for.  Held in the
	// store so the sidebar and the router agree about what is reachable; null
	// while the probe is still in flight.
	const products = companyProducts || NO_COMPANY_PRODUCTS;

	// Operators (AIP admins) can act on behalf of a single customer company.
	const canSwitch = useMemo(() => canSwitchCompany(), []);
	const activeCompany = useMemo(() => getActiveCompany(), []);
	const showCompanySwitch = canSwitch && switchableCompanyCount > 1;

	useEffect(() => {
		if (!canSwitch) {
			setSwitchableCompanyCount(0);
			return undefined;
		}
		let cancelled = false;
		// Same merged catalogue the picker itself uses, so the nav item appears
		// whenever the picker would actually offer a choice.
		fetchCompanyDirectory({
			onCached: list => {
				if (!cancelled) { setSwitchableCompanyCount(list.length); }
			}
		})
			.then(list => {
				if (!cancelled) { setSwitchableCompanyCount(list.length); }
			})
			.catch(() => {
				if (!cancelled) { setSwitchableCompanyCount(0); }
			});
		return () => {
			cancelled = true;
		};
	}, [canSwitch]);

	// Probe the viewed company for data if the router has not already done so.
	// Answers are cached per company, so this is a no-op on later mounts.
	useEffect(() => {
		if (!companyProducts) {
			getCompanyProducts();
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Sync collapse state with main body class + localStorage
	useEffect(() => {
		const mainBody = document.querySelector('.main-body');
		if (mainBody) {
			if (collapsed) {
				mainBody.classList.add('main-body-minify');
			} else {
				mainBody.classList.remove('main-body-minify');
			}
		}
		try {
			localStorage.setItem('cg_sidebar_collapsed', String(collapsed));
		} catch {
			// ignore
		}
	}, [collapsed]);

	const pathname = useMemo(
		() => (location ? location.pathname : window.location.pathname),
		[location]
	);

	const search = useMemo(
		() => (location ? location.search : window.location.search),
		[location]
	);

	// An application is offered when the company being viewed has data for it.
	const showCorrMonitor = products.hasCp || products.hasXli;
	const showTracking = products.hasTracking;

	// Active state helpers
	const isActiveExact = useCallback(path => pathname === path, [pathname]);
	const isActivePrefix = useCallback(path => pathname.startsWith(path), [pathname]);

	// CorrLogix tab detection (via ?tab= query param)
	const activeTab = useMemo(() => {
		const params = new URLSearchParams(search);
		return params.get('tab') || 'all-projects';
	}, [search]);

	// All Projects is now its own route; fallback: old CorrLogix default tab
	const isAllProjects =
		isActivePrefix('/all-projects') ||
		(isActiveExact('/CorrLogix') && activeTab === 'all-projects');
	const isMyProjects =
		isActivePrefix('/my-projects') ||
		(isActivePrefix('/CorrLogix') && activeTab === 'my-projects');
	const hasMobileProjectTools =
		isActiveExact('/SpectrumXLI') ||
		(isActivePrefix('/CorrMonitor') &&
			!isActivePrefix('/CorrMonitor/XLI/Project') &&
			!pathname.includes('/Asset/')) ||
		(isActivePrefix('/Tracking') && !isActivePrefix('/Tracking/run/'));

	const toggleCollapse = useCallback(() => {
		setCollapsed(prev => !prev);
	}, []);

	const openSignOutConfirm = useCallback(() => {
		setIsSignOutConfirmOpen(true);
	}, []);

	const closeSignOutConfirm = useCallback(() => {
		setIsSignOutConfirmOpen(false);
	}, []);

	const handleConfirmSignOut = useCallback(() => {
		AccountUtil.logoff();
		window.location = '/Account/Login';
	}, []);

	const closeMobileMenu = useCallback(() => {
		setIsMobileOpen(false);
	}, []);

	const openMobileTools = useCallback(() => {
		setIsMobileOpen(false);
		window.dispatchEvent(new window.CustomEvent('cg-mobile-project-tools'));
	}, []);

	return (
		<>
		<header className="cg-mobile-topbar">
			<button
				type="button"
				className="cg-mobile-topbar__menu"
				onClick={() => setIsMobileOpen(open => !open)}
				aria-label={isMobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
				aria-expanded={isMobileOpen}
			>
				<IconMenu />
			</button>
			<div className="cg-mobile-topbar__brand">
				<CorrGuardShield size={24} />
				<span>CORRGUARD</span>
			</div>
			<span className="cg-mobile-topbar__spacer" aria-hidden="true" />
		</header>
		{isMobileOpen && (
			<button
				type="button"
				className="cg-sidebar__mobile-backdrop"
				onClick={closeMobileMenu}
				aria-label="Close navigation menu"
			/>
		)}
		<nav className={`cg-sidebar${collapsed ? ' cg-sidebar--collapsed' : ' cg-sidebar--expanded'}${isMobileOpen ? ' cg-sidebar--mobile-open' : ''}`}>
			{/* ── Brand ─────────────────── */}
			<div className="cg-sidebar__brand">
				<div className="cg-sidebar__logo">
					<CorrGuardShield size={28} />
				</div>
				{!collapsed && (
					<div className="cg-sidebar__brand-text">
						<span className="cg-sidebar__brand-name">
							<span style={{ color: 'rgba(255,255,255,0.88)' }}>CORR</span>
							<span style={{ color: '#5ee8e2' }}>GUARD</span>
						</span>

						<span className="cg-sidebar__brand-sub">by Corrpro Canada</span>
						<span className="cg-sidebar__brand-portal">Portal</span>
					</div>
				)}
				<button
					type="button"
					className="cg-sidebar__toggle-btn"
					onClick={toggleCollapse}
					title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
				>
					<IconChevronLeft flipped={collapsed} />
				</button>
			</div>

			{/* ── Scrollable nav ─────────── */}
			<div className="cg-sidebar__content">
				{/* Projects section */}
				{!collapsed && <div className="cg-sidebar__version">{releaseVersion}</div>}
				{activeCompany && !collapsed && (
					showCompanySwitch ? (
						<Link to="/switch-company" className="cg-sidebar__active-company" title="Switch company">
							<span className="cg-sidebar__active-company-label">Viewing company</span>
							<span className="cg-sidebar__active-company-name">{activeCompany}</span>
						</Link>
					) : (
						<div className="cg-sidebar__active-company" title="Viewing company">
							<span className="cg-sidebar__active-company-label">Viewing company</span>
							<span className="cg-sidebar__active-company-name">{activeCompany}</span>
						</div>
					)
				)}
				<NavSection title="Projects" collapsed={collapsed}>
					{hasMobileProjectTools && (
						<NavItem
							className="cg-nav__item--mobile-tools"
							icon={<IconSliders />}
							label="Search & Filters"
							isActive={false}
							collapsed={collapsed}
							onClick={openMobileTools}
						/>
					)}
					<NavItem
						to="/all-projects"
						icon={<IconGrid />}
						label="All Projects"
						isActive={isAllProjects}
						collapsed={collapsed}
						onClick={closeMobileMenu}
					/>
					<NavItem
						to="/my-projects"
						icon={<IconFolder />}
						label="My Projects"
						isActive={isMyProjects}
						collapsed={collapsed}
						onClick={closeMobileMenu}
					/>
				</NavSection>

				<div className="cg-nav__separator" />

				{/* Applications section — driven by the data the viewed company has. */}
				{(showCorrMonitor || showTracking) && (
					<NavSection title="Applications" collapsed={collapsed}>
						{showCorrMonitor && (
							<NavItem
								to="/CorrMonitor"
								icon={<IconMonitor />}
								label="CorrMonitor"
								isActive={isActivePrefix('/CorrMonitor') || isActivePrefix('/AssetView')}
								collapsed={collapsed}
								onClick={closeMobileMenu}
							/>
						)}
						{showTracking && (
							<NavItem
								to="/Tracking"
								icon={<IconCrosshair />}
								label="Tracking"
								isActive={isActivePrefix('/Tracking')}
								collapsed={collapsed}
								onClick={closeMobileMenu}
							/>
						)}
					</NavSection>
				)}

				<div className="cg-nav__separator" />

				{/* Account section */}
				<NavSection title="Account" collapsed={collapsed}>
					<NavItem
						to="/my-account"
						icon={<IconUser />}
						label="My Account"
						isActive={isActivePrefix('/my-account')}
						collapsed={collapsed}
						onClick={closeMobileMenu}
					/>
					<NavItem
						to="/help-support"
						icon={<IconHelp />}
						label="Help & Support"
						isActive={isActivePrefix('/help-support')}
						collapsed={collapsed}
						onClick={closeMobileMenu}
					/>
				</NavSection>
			</div>

			{/* ── Footer ────────────────── */}
			<div className="cg-sidebar__footer">
				<button
					type="button"
					className="cg-sidebar__signout-btn"
					onClick={openSignOutConfirm}
					title="Sign out"
				>
					<IconSignOut />
					{!collapsed && <span>Sign Out</span>}
				</button>
			</div>

			<ConfirmDialog
				cancelLabel="Cancel"
				confirmLabel="Sign Out"
				confirmVariant="danger"
				description="You will be returned to the login screen."
				isOpen={isSignOutConfirmOpen}
				onCancel={closeSignOutConfirm}
				onConfirm={handleConfirmSignOut}
				title="Sign out?"
			/>
		</nav>
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(LeftPanel));
